import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Eye, Plus, Bug, Search, AlertCircle } from 'lucide-react';

export default function Support() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Immediate redirect if not authenticated and not loading
  if (!isLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
            <p className="text-gray-600 mb-4">Please log in to access support features.</p>
            <Button onClick={() => navigate('/login')} className="bg-red-600 hover:bg-red-700">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <Bug className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-secondary mb-2">Support Center</h1>
          <p className="text-gray-600 text-lg">
            Welcome to the support center. How can we help you today?
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* View Existing Tickets */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/support/tickets')}>
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-xl text-blue-900">View Existing Tickets</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">
                Browse and track your support tickets. View status updates, admin responses, and filter by type, status, or priority.
              </p>
              <div className="space-y-2 text-sm text-gray-500">
                {user?.role === 'admin' ? (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <Search className="w-4 h-4" />
                      <span>View all tickets in the system</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Bug className="w-4 h-4" />
                      <span>Update ticket status and add notes</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <Search className="w-4 h-4" />
                      <span>Search and filter your tickets</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Bug className="w-4 h-4" />
                      <span>Track progress and updates</span>
                    </div>
                  </>
                )}
              </div>
              <Button className="w-full mt-6 bg-primary hover:bg-primary-dark">
                View Tickets
              </Button>
            </CardContent>
          </Card>

          {/* Create New Ticket */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/support/bug-report')}>
            <CardHeader className="text-center pb-4">
              <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-xl text-green-900">Create New Ticket</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">
                Report a bug, request a feature, or get help with any issue you're experiencing.
              </p>
              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex items-center justify-center gap-2">
                  <Bug className="w-4 h-4" />
                  <span>Report bugs and issues</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span>Request new features</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Search className="w-4 h-4" />
                  <span>Get help and support</span>
                </div>
              </div>
              <Button className="w-full mt-6 bg-primary hover:bg-primary-dark">
                Create Ticket
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats for Admin */}
        {user?.role === 'admin' && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Quick Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">-</div>
                  <div className="text-sm text-red-600">Open Tickets</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">-</div>
                  <div className="text-sm text-yellow-600">In Progress</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary">-</div>
                  <div className="text-sm text-primary">Fixed</div>
                </div>
                <div className="text-center p-4 bg-background rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">-</div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4 text-center">
                Click "View Existing Tickets" to see detailed statistics and manage tickets.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
} 